import "../styles/customDialog.css";

/**
 * Universal Custom Dialog Utility for Vaithikasri
 * Replaces native window.alert and window.confirm with modern, glassmorphic UI modals.
 */

let activeDialogResolve = null;

function removeExistingDialog() {
  const existing = document.getElementById("vaithikasri-custom-dialog-root");
  if (existing) {
    existing.remove();
  }
  if (activeDialogResolve) {
    activeDialogResolve(false);
    activeDialogResolve = null;
  }
}

/**
 * Show a custom confirmation dialog
 * @param {Object|string} options
 * @returns {Promise<boolean>}
 */
export function showConfirm(options) {
  return new Promise((resolve) => {
    removeExistingDialog();
    activeDialogResolve = resolve;

    const opts = typeof options === "string" ? { message: options } : (options || {});
    const title = opts.title || (opts.isDangerous !== false ? "Are you sure?" : "Confirm Action");
    const message = opts.message || "Are you sure you want to proceed?";
    const subMessage = opts.subMessage || "";
    const confirmText = opts.confirmText || opts.confirmLabel || (opts.isDangerous !== false ? "Yes, Delete" : "Confirm");
    const cancelText = opts.cancelText || opts.cancelLabel || "Cancel";
    const type = opts.type || (opts.isDangerous !== false ? "danger" : "primary");

    const root = document.createElement("div");
    root.id = "vaithikasri-custom-dialog-root";
    root.className = "vaithikasri-custom-dialog-root";
    root.style.cssText = "position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:99999999;display:flex;align-items:center;justify-content:center;pointer-events:auto;";

    const iconHtml =
      type === "danger"
        ? '<i class="bi bi-trash3-fill"></i>'
        : type === "warning"
        ? '<i class="bi bi-exclamation-triangle-fill"></i>'
        : type === "success"
        ? '<i class="bi bi-check-circle-fill"></i>'
        : '<i class="bi bi-question-circle-fill"></i>';

    const subMessageHtml = subMessage
      ? `<div class="custom-dialog-detail-box">
          <i class="bi bi-exclamation-triangle-fill"></i>
          <span>${subMessage}</span>
         </div>`
      : "";

    root.innerHTML = `
      <div class="custom-dialog-backdrop" id="custom-dialog-backdrop">
        <div class="custom-dialog-card" id="custom-dialog-card" role="dialog" aria-modal="true">
          <button type="button" class="custom-dialog-close-btn" id="custom-dialog-close-btn" title="Close">
            <i class="bi bi-x-lg"></i>
          </button>
          <div class="custom-dialog-icon-bubble ${type}">
            ${iconHtml}
          </div>
          <h3 class="custom-dialog-title">${title}</h3>
          <p class="custom-dialog-message">${message}</p>
          ${subMessageHtml}
          <div class="custom-dialog-actions">
            <button type="button" class="custom-dialog-btn-cancel" id="custom-dialog-btn-cancel">
              ${cancelText}
            </button>
            <button type="button" class="custom-dialog-btn-confirm ${type}" id="custom-dialog-btn-confirm">
              ${type === "danger" ? '<i class="bi bi-trash3"></i>' : ""}
              ${confirmText}
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(root);

    const cleanup = (result) => {
      window.removeEventListener("keydown", handleKeyDown);
      if (root && root.parentNode) {
        root.remove();
      }
      activeDialogResolve = null;
      resolve(result);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cleanup(false);
      } else if (e.key === "Enter") {
        e.preventDefault();
        cleanup(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    document.getElementById("custom-dialog-backdrop").addEventListener("click", (e) => {
      if (e.target.id === "custom-dialog-backdrop") {
        cleanup(false);
      }
    });

    document.getElementById("custom-dialog-close-btn").addEventListener("click", () => cleanup(false));
    document.getElementById("custom-dialog-btn-cancel").addEventListener("click", () => cleanup(false));
    document.getElementById("custom-dialog-btn-confirm").addEventListener("click", () => cleanup(true));

    // Focus confirm button for accessibility
    setTimeout(() => {
      const confirmBtn = document.getElementById("custom-dialog-btn-confirm");
      if (confirmBtn) confirmBtn.focus();
    }, 50);
  });
}

/**
 * Show a custom alert dialog
 * @param {Object|string} options
 * @returns {Promise<void>}
 */
export function showAlert(options) {
  return new Promise((resolve) => {
    removeExistingDialog();
    activeDialogResolve = resolve;

    const opts = typeof options === "string" ? { message: options } : (options || {});
    const message = opts.message || "";
    const isSuccessMsg = /success/i.test(String(message));
    const isErrorMsg = /error|failed|failure/i.test(String(message));
    const defaultType = isSuccessMsg ? "success" : isErrorMsg ? "danger" : "info";
    const defaultTitle = isSuccessMsg ? "Success" : isErrorMsg ? "Error" : "Notice";

    const title = opts.title || defaultTitle;
    const okText = opts.okText || "OK";
    const type = opts.type || defaultType;

    const root = document.createElement("div");
    root.id = "vaithikasri-custom-dialog-root";
    root.className = "vaithikasri-custom-dialog-root";
    root.style.cssText = "position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:99999999;display:flex;align-items:center;justify-content:center;pointer-events:auto;";

    const iconHtml =
      type === "danger" || type === "error"
        ? '<i class="bi bi-exclamation-octagon-fill"></i>'
        : type === "warning"
        ? '<i class="bi bi-exclamation-triangle-fill"></i>'
        : type === "success"
        ? '<i class="bi bi-check-circle-fill"></i>'
        : '<i class="bi bi-info-circle-fill"></i>';

    const bubbleClass = type === "error" ? "danger" : type;

    root.innerHTML = `
      <div class="custom-dialog-backdrop" id="custom-dialog-backdrop">
        <div class="custom-dialog-card" id="custom-dialog-card" role="dialog" aria-modal="true">
          <button type="button" class="custom-dialog-close-btn" id="custom-dialog-close-btn" title="Close">
            <i class="bi bi-x-lg"></i>
          </button>
          <div class="custom-dialog-icon-bubble ${bubbleClass}">
            ${iconHtml}
          </div>
          <h3 class="custom-dialog-title">${title}</h3>
          <p class="custom-dialog-message">${message}</p>
          <div class="custom-dialog-actions" style="justify-content: center;">
            <button type="button" class="custom-dialog-btn-confirm primary" id="custom-dialog-btn-ok" style="flex: 1; max-width: 200px;">
              ${okText}
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(root);

    const cleanup = () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (root && root.parentNode) {
        root.remove();
      }
      activeDialogResolve = null;
      resolve();
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        cleanup();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    document.getElementById("custom-dialog-backdrop").addEventListener("click", (e) => {
      if (e.target.id === "custom-dialog-backdrop") {
        cleanup();
      }
    });

    document.getElementById("custom-dialog-close-btn").addEventListener("click", cleanup);
    document.getElementById("custom-dialog-btn-ok").addEventListener("click", cleanup);

    setTimeout(() => {
      const okBtn = document.getElementById("custom-dialog-btn-ok");
      if (okBtn) okBtn.focus();
    }, 50);
  });
}

/**
 * Show a custom prompt dialog
 * @param {Object|string} options
 * @param {string} [defaultValue=""]
 * @returns {Promise<string|null>}
 */
export function showPrompt(options, defaultValue = "") {
  return new Promise((resolve) => {
    removeExistingDialog();
    activeDialogResolve = resolve;

    const opts = typeof options === "string" ? { message: options, defaultValue } : (options || {});
    const title = opts.title || "Input Required";
    const message = opts.message || "Please enter a value:";
    const val = opts.defaultValue !== undefined ? opts.defaultValue : (defaultValue || "");
    const placeholder = opts.placeholder || "";
    const confirmText = opts.confirmText || opts.okText || "Submit";
    const cancelText = opts.cancelText || "Cancel";

    const root = document.createElement("div");
    root.id = "vaithikasri-custom-dialog-root";
    root.className = "vaithikasri-custom-dialog-root";
    root.style.cssText = "position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:99999999;display:flex;align-items:center;justify-content:center;pointer-events:auto;";

    root.innerHTML = `
      <div class="custom-dialog-backdrop" id="custom-dialog-backdrop">
        <div class="custom-dialog-card" id="custom-dialog-card" role="dialog" aria-modal="true">
          <button type="button" class="custom-dialog-close-btn" id="custom-dialog-close-btn" title="Close">
            <i class="bi bi-x-lg"></i>
          </button>
          <div class="custom-dialog-icon-bubble info">
            <i class="bi bi-pencil-square"></i>
          </div>
          <h3 class="custom-dialog-title">${title}</h3>
          <p class="custom-dialog-message">${message}</p>
          <input type="text" class="custom-dialog-input" id="custom-dialog-input" value="${String(val).replace(/"/g, '&quot;')}" placeholder="${placeholder}" />
          <div class="custom-dialog-actions">
            <button type="button" class="custom-dialog-btn-cancel" id="custom-dialog-btn-cancel">
              ${cancelText}
            </button>
            <button type="button" class="custom-dialog-btn-confirm primary" id="custom-dialog-btn-confirm">
              ${confirmText}
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(root);

    const inputEl = document.getElementById("custom-dialog-input");

    const cleanup = (result) => {
      window.removeEventListener("keydown", handleKeyDown);
      if (root && root.parentNode) {
        root.remove();
      }
      activeDialogResolve = null;
      resolve(result);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cleanup(null);
      } else if (e.key === "Enter") {
        e.preventDefault();
        cleanup(inputEl ? inputEl.value : null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    document.getElementById("custom-dialog-backdrop").addEventListener("click", (e) => {
      if (e.target.id === "custom-dialog-backdrop") {
        cleanup(null);
      }
    });

    document.getElementById("custom-dialog-close-btn").addEventListener("click", () => cleanup(null));
    document.getElementById("custom-dialog-btn-cancel").addEventListener("click", () => cleanup(null));
    document.getElementById("custom-dialog-btn-confirm").addEventListener("click", () => cleanup(inputEl ? inputEl.value : null));

    setTimeout(() => {
      if (inputEl) {
        inputEl.focus();
        inputEl.select();
      }
    }, 50);
  });
}

// Global browser window bindings
if (typeof window !== "undefined") {
  window.showConfirm = showConfirm;
  window.showAlert = showAlert;
  window.showPrompt = showPrompt;

  // Global override of window.alert so all existing alert() calls automatically render the custom UI modal
  window.alert = (message) => {
    showAlert({ message: String(message) });
  };
}

const customDialog = { showConfirm, showAlert, showPrompt };
export default customDialog;
